import { Portal, Select as ArkSelect } from '@ark-ui/react';
import { LuChevronDown, LuCheck } from 'react-icons/lu';
import { css } from '@styled-system/css';
import { Box, BoxProps } from '@styled-system/jsx';
import { hstack, vstack } from '@styled-system/patterns';

export type SelectItem = {
  label: string;
  value: string;
};

type Props = {
  items: SelectItem[];
  defaultValue?: string;
  label?: string;
  placeholder?: string;
  onSelectItem?: (item: SelectItem) => void;
} & BoxProps;

export const Select = ({
  items,
  defaultValue,
  label,
  placeholder,
  onSelectItem,
  ...boxProps
}: Props) => {
  const _defaultValue = defaultValue ?? items[0]?.value;

  return (
    <Box {...boxProps}>
      <ArkSelect.Root
        items={items}
        defaultValue={_defaultValue ? [_defaultValue] : []}
        onValueChange={(e) => {
          const item = e.items[0];
          if (item) {
            onSelectItem?.(item);
          }
        }}
        // NOTE: Implement when needed.
        multiple={false}
      >
        {label && (
          <ArkSelect.Label
            className={css({
              fontSize: 'size_12',
              color: 'content_secondary',
            })}
          >
            {label}
          </ArkSelect.Label>
        )}
        <ArkSelect.Control>
          <ArkSelect.Trigger className={triggerStyle}>
            <ArkSelect.ValueText placeholder={placeholder} />
            <ArkSelect.Indicator>
              <LuChevronDown />
            </ArkSelect.Indicator>
          </ArkSelect.Trigger>
        </ArkSelect.Control>
        <Portal>
          <ArkSelect.Positioner>
            <ArkSelect.Content className={contentStyle}>
              <ArkSelect.ItemGroup className={itemGroupStyle}>
                {items.map((item) => (
                  <ArkSelect.Item
                    key={item.value}
                    item={item}
                    className={itemStyle}
                  >
                    <ArkSelect.ItemText>{item.label}</ArkSelect.ItemText>
                    <ArkSelect.ItemIndicator>
                      <LuCheck />
                    </ArkSelect.ItemIndicator>
                  </ArkSelect.Item>
                ))}
              </ArkSelect.ItemGroup>
            </ArkSelect.Content>
          </ArkSelect.Positioner>
        </Portal>
        <ArkSelect.HiddenSelect />
      </ArkSelect.Root>
    </Box>
  );
};

const triggerStyle = css({
  appearance: 'none',
  display: 'inline-flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  cursor: 'pointer',
  background: 'surface_primary',
  p: '3',
  borderRadius: '8px',
  border: '1px solid token(colors.separator)',
  color: 'content_primary',
  fontWeight: 400,
  fontSize: 'size_14',
});

const contentStyle = css({
  background: 'surface_primary',
  py: '3',
  px: '1',
  borderRadius: '8px',
  boxShadow: '0 0 4px rgba(0, 0, 0, 0.15)',
  minW: '100px',
  _open: {
    animation: 'fadeIn 0.25s ease-out',
  },
  _closed: {
    animation: 'fadeOut 0.2s ease-out',
  },
  _focusVisible: {
    outlineOffset: '2px',
    outline: '2px solid',
    outlineColor: 'border.outline',
  },
});

const itemGroupStyle = vstack({
  gap: '1',
  w: '100%',
});

const itemStyle = hstack({
  py: '1',
  px: '2',
  w: '100%',
  justifyContent: 'space-between',
  cursor: 'pointer',
  borderRadius: 'l2',
  _hover: {
    background: 'gray.a3',
    color: 'fg.default',
  },
  _highlighted: {
    background: 'gray.a3',
    color: 'fg.default',
  },
  _selected: {
    color: 'fg.default',
  },
  _disabled: {
    color: 'fg.disabled',
    cursor: 'not-allowed',
    _hover: {
      background: 'transparent',
      color: 'fg.disabled',
    },
  },
});
